import React, { useMemo } from 'react';
import { Pressable, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { XStack, tokens } from '@fhs/ui';

/** WCAG 2.5.5 minimum touch target; must match interactiveDotHitArea minWidth/minHeight. */
const INTERACTIVE_HIT = 44;
/** Space between dot edges when non-interactive (container gap); keep interactive layout visually aligned. */
const DOT_EDGE_GAP = 12;

interface Props {
  totalPages: number;
  selectedPage: number;
  onPressPage?: (page: number) => void;
  dotSize?: number;
  style?: StyleProp<ViewStyle>;
}

export const PaginationDots = ({
  selectedPage,
  totalPages,
  onPressPage,
  dotSize,
  style,
}: Props) => {
  const interactive = Boolean(onPressPage);

  const paginationDots = useMemo(
    () =>
      [...Array(totalPages).keys()].map(page => {
        const size = dotSize ?? 12;
        const isSelected = page === selectedPage;
        const backgroundColor = isSelected
          ? tokens.colors.$houseRedDarken
          : tokens.colors.$blackOpacity10;

        const dotVisual = (
          <View
            style={{
              height: size,
              width: size,
              backgroundColor,
              borderRadius: size / 2,
            }}
          />
        );

        if (!interactive) {
          return (
            <View
              key={page}
              accessible={false}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            >
              {dotVisual}
            </View>
          );
        }

        /** Pull pressables together so gap isn’t measured between 44px hit boxes (which looked like huge dot spacing). */
        const overlapMargin =
          page > 0 ? Math.min(0, size + DOT_EDGE_GAP - INTERACTIVE_HIT) : 0;

        return (
          <Pressable
            key={page}
            accessibilityRole="button"
            accessibilityLabel={`Page ${page + 1} of ${totalPages}`}
            accessibilityHint="Shows this slide in the carousel"
            accessibilityState={{ selected: isSelected, disabled: false }}
            onPress={() => onPressPage?.(page)}
            style={[styles.interactiveDotHitArea, overlapMargin !== 0 && { marginLeft: overlapMargin }]}
          >
            {dotVisual}
          </Pressable>
        );
      }),
    [interactive, onPressPage, selectedPage, totalPages, dotSize]
  );
  return (
    <XStack style={[interactive ? styles.containerInteractive : styles.containerPassive, style]}>
      {paginationDots}
    </XStack>
  );
};

const styles = StyleSheet.create({
  containerPassive: {
    gap: DOT_EDGE_GAP,
  },
  /** No flex gap: spacing comes from per-dot overlap so 44×44 targets don’t push visuals apart. */
  containerInteractive: {
    gap: 0,
    justifyContent: 'center',
  },
  /** Minimum 44×44 touch / keyboard focus target (WCAG 2.5.5); visual dot stays smaller inside. */
  interactiveDotHitArea: {
    minWidth: INTERACTIVE_HIT,
    minHeight: INTERACTIVE_HIT,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
